import { MlIconCardProps } from '../../molecules'
import { Desk } from './icons/desk'
import { ConferenceRoom } from './icons/conference-room'
import { EventSpace } from './icons/event-space'
import { Office } from './icons/office'
import { Suite } from './icons/suite'
import {
  AtPillCalendar,
  AtPillCalendarProps,
  AtPillDropdown,
  AtPillDropdownProps,
  AtToggleProps,
  BusinessHours,
  DropdownOption,
  Holiday,
  capacityMap,
  timeDurationMap,
  timeslotsMap,
} from '../../atoms'
import { FilterChangeHandler, FilterState } from './filter'

export type SpaceType = 'day_pass' | 'meeting_room' | 'event_space' | 'office' | 'suite'

export const spaceTypes: (Pick<MlIconCardProps, 'label' | 'IconRef'> & { typeKey: SpaceType })[] = [
  { label: 'Day Pass', IconRef: Desk, typeKey: 'day_pass' },
  { label: 'Meeting Room', IconRef: ConferenceRoom, typeKey: 'meeting_room' },
  { label: 'Event Space', IconRef: EventSpace, typeKey: 'event_space' },
  { label: 'Office', IconRef: Office, typeKey: 'office' },
  { label: 'Suite', IconRef: Suite, typeKey: 'suite' },
]

export type FilterMapProps =
  | ({ type: 'date' } & Omit<AtPillCalendarProps, 'holidays'>)
  | ({ type: 'dropdown' } & AtPillDropdownProps)

export const filterMap = (props: FilterMapProps, i: number, holidays?: Holiday) => {
  if (props.type === 'date') return <AtPillCalendar key={i} {...props} holidays={holidays} />
  return <AtPillDropdown key={i} {...props} />
}

const eventSpaceOptions = [
  { label: '60 and under', value: '0-60' },
  { label: '61-150', value: '61-150' },
  { label: '151 or more', value: '151' },
]
const officeAndSuiteCapacity = [
  { label: '5 and under', value: '0-5' },
  { label: '6-20', value: '6-20' },
  { label: '21 or more', value: '21' },
]

const meetingRoomCapacity = Array.from({ length: 11 }, capacityMap)

export const capacity: Record<SpaceType, DropdownOption[]> = {
  day_pass: [],
  event_space: eventSpaceOptions,
  suite: officeAndSuiteCapacity,
  office: officeAndSuiteCapacity,
  meeting_room: meetingRoomCapacity,
}

export const filterConfiguration: (
  businessHours?: BusinessHours,
  state?: FilterState,
  onChange?: FilterChangeHandler,
  isBDP?: boolean
) => Record<SpaceType, FilterMapProps[]> = (businessHours, state, onChange, isBDP) => {
  const changeFn = (field: keyof FilterState) => (value: FilterState[typeof field]) =>
    onChange?.({
      [field]: value,
    })

  return {
    day_pass: [
      {
        type: 'date',
        id: 'or-filter-section-date-calendar',
        mode: 'range',
        label: 'Date(s)',
        value: state?.date,
        onChange: changeFn('date'),
        open: state?.calendarOpen,
        onClick: changeFn('calendarOpen'),
        disabled: state?.loading || (state?.disabled && !isBDP),
      },
    ],
    meeting_room: [
      {
        type: 'date',
        id: 'or-filter-section-date-calendar',
        mode: 'range',
        label: 'Date(s)',
        value: state?.date,
        onChange: changeFn('date'),
        open: state?.calendarOpen,
        onClick: changeFn('calendarOpen'),
        disabled: state?.loading || (state?.disabled && !isBDP),
      },
      ...(!state?.date || !(state?.date && 'to' in state.date && state.date.to)
        ? [
            {
              type: 'dropdown',
              id: 'or-filter-section-duration-dropdown',
              label: 'Duration',
              options: Array.from({ length: 16 }, timeDurationMap),
              value: state?.duration,
              onChange: changeFn('duration'),
              disabled: state?.loading || (state?.disabled && !isBDP),
            },
            {
              type: 'dropdown',
              id: 'or-filter-section-start-time-dropdown',
              label: 'Start Time',
              options: [...timeslotsMap(businessHours?.startHour as string, businessHours?.endHour as string)],
              value: state?.startTime,
              onChange: changeFn('startTime'),
              disabled: state?.loading || (state?.disabled && !isBDP),
            },
          ]
        : []),
    ].filter((x) => x) as FilterMapProps[],
    event_space: [
      {
        type: 'date',
        id: 'or-filter-section-date-calendar',
        mode: 'range',
        label: 'Date(s)',
        value: state?.date,
        onChange: changeFn('date'),
        open: state?.calendarOpen,
        onClick: changeFn('calendarOpen'),
        disabled: state?.loading || (state?.disabled && !isBDP),
      },
    ],
    office: [
      {
        id: 'or-filter-section-date-calendar',
        type: 'date',
        mode: 'range',
        label: 'Date(s)',
        value: state?.date,
        onChange: changeFn('date'),
        open: state?.calendarOpen,
        onClick: changeFn('calendarOpen'),
        disabled: state?.loading || (state?.disabled && !isBDP),
      },
    ],
    suite: [],
  }
}

export const instantBook: AtToggleProps = {
  label: 'Instant Book',
  hint: { content: 'Filter for spaces you can book now', placement: 'bottom' },
}

export const initialFilterState: FilterState = {
  type: 'meeting_room',
  loading: true,
  calendarOpen: false,
  disabled: true,
}
